import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { useAuth } from '@/auth/use-auth';
import { Button } from '@/components/ui/button';
import { AuthCard } from '@/pages/auth/auth-card';
import { useResendVerification } from '@/pages/auth/use-resend-verification';

/**
 * `failed` = the backend rejected the link (bad signature, expired, stale) -
 * only a fresh link helps. `error` = it could not answer (throttled, down,
 * offline) - the same link may still work later.
 */
type Status = 'verifying' | 'success' | 'failed' | 'error' | 'pending';

/** The one status the verify endpoint uses for a dead link (verification_link_invalid). */
const HTTP_LINK_REJECTED = 403;

/** Where a guest lands after logging in to request a new link. */
const RESEND_PATH = '/email/verify';

/** The signed path FortifyServiceProvider mails (`api.email.verify-link`). */
const VERIFY_LINK_PATH = /^\/api\/email\/verify-link\/\d+\/[0-9a-f]+$/;

/**
 * The `?url=` param is attacker-controllable (this page is reached from an
 * emailed link), so it must never be fetched blindly with credentials - that
 * would turn the SPA into a same-origin confused-deputy able to fire an
 * authenticated GET at any endpoint. Only accept a same-origin verification
 * link path; anything else is treated as "no url".
 */
function safeVerifyUrl(raw: string | null): string | null {
  if (raw === null) return null;
  try {
    const parsed = new URL(raw, window.location.origin);
    if (parsed.origin !== window.location.origin) return null;
    if (!VERIFY_LINK_PATH.test(parsed.pathname)) return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

function initialStatus(isLegacyLink: boolean, verifyUrl: string | null): Status {
  // Pre-cutover links (`/email/verify/{id}?expires=&signature=`) were signed
  // with the old site's key and carry no email hash, so the new backend
  // cannot validate them: go straight to the "request a new link" hint.
  if (isLegacyLink) return 'failed';
  return verifyUrl ? 'verifying' : 'pending';
}

async function replayVerifyUrl(verifyUrl: string): Promise<Status> {
  try {
    const response = await fetch(verifyUrl, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    if (response.ok) return 'success';
    return response.status === HTTP_LINK_REJECTED ? 'failed' : 'error';
  } catch {
    return 'error';
  }
}

/**
 * Landing for the verification email. The notification link is
 * `/email/verify?url=<signed api path>` (FortifyServiceProvider); this page
 * replays that path, which works with or without a session - the link is
 * valid for a week on any device (legacy parity). With no `url` it shows the
 * "check your inbox" state.
 */
export default function VerifyEmailPage() {
  const { t } = useTranslation('auth');
  const { status: authStatus, refresh } = useAuth();
  const { id: legacyId } = useParams<{ id?: string }>();

  const params = new URLSearchParams(window.location.search);
  const verifyUrl = legacyId === undefined ? safeVerifyUrl(params.get('url')) : null;

  const [status, setStatus] = useState<Status>(() =>
    initialStatus(legacyId !== undefined, verifyUrl),
  );
  const startedRef = useRef(false);

  useEffect(() => {
    if (verifyUrl === null || startedRef.current) return;
    startedRef.current = true;

    void (async () => {
      const outcome = await replayVerifyUrl(verifyUrl);
      // A logged-in tab picks up email_verified_at; a guest's refresh is a no-op.
      if (outcome === 'success') await refresh();
      setStatus(outcome);
    })();
  }, [verifyUrl, refresh]);

  return (
    <AuthCard title={t('verifyEmail.title')}>
      <div className="flex flex-col gap-4">
        {status === 'verifying' && (
          <p className="font-mono text-sm text-dim">{t('verifyEmail.verifying')}</p>
        )}

        {status === 'success' && (
          <>
            <p className="font-mono text-sm text-green">{t('verifyEmail.success')}</p>
            {authStatus === 'guest' && (
              <Button asChild variant="primary">
                <Link to="/login">{t('login.title')}</Link>
              </Button>
            )}
          </>
        )}

        {status === 'failed' && (
          <>
            <p className="font-mono text-sm text-destructive" role="alert">
              {t('verifyEmail.failed')}
            </p>
            <NewLinkHint />
          </>
        )}

        {status === 'error' && (
          <p className="font-mono text-sm text-destructive" role="alert">
            {t('verifyEmail.error')}
          </p>
        )}

        {status === 'pending' && (
          <>
            <p className="font-mono text-sm text-dim">{t('verifyEmail.pending')}</p>
            <NewLinkHint />
          </>
        )}
      </div>
    </AuthCard>
  );
}

/**
 * How to get a fresh link: resending needs a session (Fortify's
 * `verification.send` is auth-only), so a guest is sent to log in first and
 * brought back here; an already-verified account needs nothing.
 */
function NewLinkHint() {
  const { t } = useTranslation('auth');
  const { status: authStatus, user } = useAuth();
  const { sending, resend } = useResendVerification();

  if (authStatus === 'guest') {
    return (
      <>
        <p className="font-mono text-sm text-dim">{t('verifyEmail.loginToResend')}</p>
        <Button asChild variant="primary">
          <Link to="/login" state={{ from: RESEND_PATH }}>
            {t('login.title')}
          </Link>
        </Button>
      </>
    );
  }

  if (authStatus !== 'authenticated') return null;

  if (user?.email_verified_at) {
    return <p className="font-mono text-sm text-green">{t('verifyEmail.alreadyVerified')}</p>;
  }

  return (
    <Button variant="outline" disabled={sending} onClick={resend}>
      {t('verifyEmail.resend')}
    </Button>
  );
}
